import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Spinner, Button, Space, Modal } from '@ag1/ui';
import { useSessionValue } from 'appLayer/auth';
import { useStoreValue } from 'state/hooks/useStore';
import { useMutationSendShopifyEmail } from 'state/internal/sendShopifyPaymentEmail';

import { getPaymentMethodV2Data } from '@portal/shared/utils/customer/getPaymentMethodV2Data';

import { PaymentMethodModalV2Props } from './types';
import styles from './PaymentMethodModalV2.module.scss';

const PaymentMethodModalV2 = ({
  mode,
  paymentMethodId,
  paymentMethod,
  isOpen,
  onClose
}: PaymentMethodModalV2Props) => {
  const { t } = useTranslation(['modalWindows']);

  const paymentMethodData = paymentMethod
    ? getPaymentMethodV2Data(paymentMethod, { bulletsType: 'short' })
    : null;

  const session = useSessionValue();
  const store = useStoreValue();
  const email = session?.customer.email;

  const modalTitle = useMemo(
    () => (mode === 'add-payment' ? t('addPaymentMethod.title') : t('editPaymentMethod.title')),
    [t, mode]
  );

  const modalInstructions = useMemo(() => {
    let instructionText;

    if (mode === 'add-payment') {
      instructionText = t('addPaymentMethod.instructions', { email });
    } else if (paymentMethodData?.paymentType === 'shopPay') {
      instructionText = t('editPaymentMethod.shopPayInstructions', { email });
    } else {
      instructionText = t('editPaymentMethod.instructions', { email });
    }

    return instructionText
      .split('\n')
      .map((line) => (line.trim() === '' ? <br key={line} /> : <p key={line}>{line}</p>));
  }, [t, mode, email, paymentMethodData]);

  const sendPaymentChangeEmailMutation = useMutationSendShopifyEmail();

  const onSubmit = useCallback(() => {
    if (mode === 'update-payment' && paymentMethodId) {
      sendPaymentChangeEmailMutation.mutateAsync({
        store,
        paymentMethodId,
        onClose
      });
    }
  }, [mode, sendPaymentChangeEmailMutation, store, paymentMethodId, onClose]);

  const isSubmitting = useMemo(
    () => sendPaymentChangeEmailMutation.isPending,
    [sendPaymentChangeEmailMutation]
  );

  return (
    <Modal mode="center" isOpen={isOpen} onClose={onClose}>
      <Modal.Header>{modalTitle}</Modal.Header>
      <Modal.Content hasFooter>{modalInstructions}</Modal.Content>
      <Modal.Footer>
        <Space className={styles.buttons}>
          <Button
            variant="outline"
            size={{ s: 'medium', xxs: 'small' }}
            theme="new"
            fluid
            onClick={onClose}>
            {t('paymentMethod.cancelButton')}
          </Button>
          <Button
            type="submit"
            theme="new"
            size={{ s: 'medium', xxs: 'small' }}
            variant="primary"
            fluid
            disabled={isSubmitting}
            onClick={onSubmit}>
            {isSubmitting ? (
              <Spinner color="brand-white" size={24} withOverlay={false} />
            ) : (
              t('paymentMethod.submitButton')
            )}
          </Button>
        </Space>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentMethodModalV2;
